
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Eye, Plus } from "lucide-react";
import { usePropertyViews } from "@/hooks/usePropertyViews";
import { ImageLightbox } from "./ImageLightbox";

// Internal component to encapsulate the usePropertyViews hook (follows React Rules of Hooks)
const ViewCountBadge = ({ propertyId }: { propertyId: string }) => {
  const { viewCount, isLoading } = usePropertyViews(propertyId);
  return (
    <Badge className="bg-black/60 text-white">
      <Eye className="h-3 w-3 mr-1" />
      {isLoading ? "..." : viewCount} Views
    </Badge>
  );
};

interface PropertyImageGalleryProps {
  propertyImages: string[];
  propertyAddress: string;
  propertyStatus: string;
  propertyId: string;
  videoUrl?: string | null;
  videoThumbnailUrl?: string | null;
  totalImageCount?: number;
  hasMoreImages?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  showViewCount?: boolean;
  isAuthenticated?: boolean;
}

export const PropertyImageGallery = ({
  propertyImages,
  propertyAddress,
  propertyStatus,
  propertyId,
  videoUrl,
  videoThumbnailUrl,
  totalImageCount,
  hasMoreImages,
  onLoadMore,
  isLoadingMore,
  showViewCount = false,
  isAuthenticated = true
}: PropertyImageGalleryProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  // If video exists, it will be index 0, images start at index 1
  const hasVideo = !!videoUrl;
  const totalItems = hasVideo ? propertyImages.length + 1 : propertyImages.length;

  const handleImageClick = (index: number) => {
    setLightboxStartIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <>
      <div className="space-y-2">
        <div 
          className="aspect-video w-full overflow-hidden rounded-lg relative cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => {
            // Only open lightbox if it's an image (not video)
            if (!hasVideo || activeImageIndex > 0) {
              handleImageClick(hasVideo ? activeImageIndex - 1 : activeImageIndex);
            }
          }}
        >
          {hasVideo && activeImageIndex === 0 ? (
            // Show video player when first item is active
            <video 
              controls
              playsInline
              // @ts-ignore - webkit-playsinline is a non-standard attribute for legacy iOS
              webkit-playsinline=""
              className="w-full h-full object-cover"
              preload="metadata"
              poster={videoThumbnailUrl || propertyImages[0] || undefined}
            >
              <source src={videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            // Show image
            propertyImages.length > 0 && (
              <img
                src={propertyImages[hasVideo ? activeImageIndex - 1 : activeImageIndex]}
                alt={`Property at ${propertyAddress}`}
                className="object-cover w-full h-full"
                fetchPriority="high"
              />
            )
          )}
          <div className="absolute top-4 right-4 flex space-x-2">
            <Badge className="bg-godyspo-gold text-black">
              {propertyStatus.charAt(0).toUpperCase() + propertyStatus.slice(1)}
            </Badge>
            {showViewCount && <ViewCountBadge propertyId={propertyId} />}
          </div>
        </div>
        {totalItems > 1 && (
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {/* Video thumbnail (if video exists) */}
            {hasVideo && (
              <div 
                className={`relative cursor-pointer h-20 w-32 flex-shrink-0 overflow-hidden rounded hover:opacity-80 transition-opacity ${
                  activeImageIndex === 0 ? 'ring-2 ring-godyspo-gold' : ''
                }`}
                onClick={() => setActiveImageIndex(0)}
              >
                <div className="relative w-full h-full">
                  <img
                    src={videoThumbnailUrl || propertyImages[0]}
                    alt="Video thumbnail"
                    className="object-cover h-full w-full"
                  />
                  {/* Video play icon overlay */}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-godyspo-gold/90 flex items-center justify-center">
                      <svg className="w-5 h-5 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Image thumbnails */}
            {propertyImages.map((image, index) => (
              <div 
                key={index}
                className={`relative cursor-pointer h-20 w-32 flex-shrink-0 overflow-hidden rounded hover:opacity-80 transition-opacity ${
                  activeImageIndex === (hasVideo ? index + 1 : index) ? 'ring-2 ring-godyspo-gold' : ''
                }`}
                onClick={() => {
                  // Thumbnail click only changes main view - lightbox opens when clicking main image
                  const newIndex = hasVideo ? index + 1 : index;
                  setActiveImageIndex(newIndex);
                }}
              >
                    <img
                      src={image}
                      alt={`Property thumbnail ${index + 1}`}
                      className="object-cover h-full w-full"
                      loading="lazy"
                    />
              </div>
            ))}
            
            {/* Load More Button (authenticated) */}
            {hasMoreImages && onLoadMore && (
              <button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="relative h-20 w-32 flex-shrink-0 rounded border-2 border-dashed border-godyspo-gold/50 hover:border-godyspo-gold transition-colors flex items-center justify-center bg-black/5 disabled:opacity-50"
                title={`Load ${totalImageCount! - propertyImages.length} more images`}
              >
                {isLoadingMore ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-godyspo-gold"></div>
                ) : (
                  <div className="text-center">
                    <Plus className="h-5 w-5 text-godyspo-gold mx-auto mb-1" />
                    <span className="text-xs text-godyspo-gold font-medium">
                      +{totalImageCount! - propertyImages.length}
                    </span>
                  </div>
                )}
              </button>
            )}
            
            {/* Sign-up CTA tile (non-authenticated) */}
            {!isAuthenticated && totalImageCount !== undefined && totalImageCount > propertyImages.length && (
              <a
                href="/buyer/register"
                className="relative h-20 w-32 flex-shrink-0 rounded border-2 border-dashed border-godyspo-gold/50 hover:border-godyspo-gold transition-colors flex items-center justify-center bg-black/5"
              >
                <div className="text-center px-1">
                  <Plus className="h-4 w-4 text-godyspo-gold mx-auto mb-0.5" />
                  <span className="text-[10px] text-godyspo-gold font-medium leading-tight block">
                    Sign up to see all {totalImageCount} photos
                  </span>
                </div>
              </a>
            )}
          </div>
        )}
      </div>

      <ImageLightbox
        images={propertyImages}
        initialIndex={lightboxStartIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        propertyAddress={propertyAddress}
        hasMoreImages={hasMoreImages}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        totalImageCount={totalImageCount}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
};
